'use client'

import { getClientAPI } from '@/app/api/client'
import { HttpTypes } from '@medusajs/types'
import { useTranslations } from 'next-intl'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useLocale } from './locale-provider'

type CartContextType = {
  cart?: ExtendedStoreCart;
  setCart: React.Dispatch<
    React.SetStateAction<ExtendedStoreCart | undefined>
  >;
  refreshCart: () => void;
  addItem: (variantId: string, quantity: number, metadata?: Record<string, string>) => Promise<void>;
  removeItem: (lineItemId: string) => Promise<void>;
  updateItem: (lineItemId: string, quantity: number) => Promise<void>;
  updateItemQuantity: (lineItemId: string, quantity: number) => Promise<void>;
  applyPromoCode: (code: string) => Promise<boolean>;
};

const CartContext = createContext<CartContextType | null>(null)

export const cartSelectParams = {
  fields: ["*items", "*shipping_methods", "+items.variant.inventory_quantity"].join(','),
}

type CartProviderProps = {
  children: React.ReactNode;
};

type ExtendedStoreCart = HttpTypes.StoreCart & {
  customer?: {
    id: string;
    email?: string;
  }
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cart, setCart] = useState<ExtendedStoreCart>()
  const { currentLocale } = useLocale()

  const t = useTranslations('Cart.toast')

  useEffect(() => {
    const api = getClientAPI()
    const cartId = localStorage.getItem('cart_id')
    if (!cartId) {
      return
    }
    api.store.cart.retrieve(cartId, cartSelectParams).then(({ cart }) => {
      setCart(cart)
    }).catch(error => {
      console.error('Error fetching cart:', error)
      localStorage.removeItem('cart_id')
    })
  }, [])

  const refreshCart = useCallback(() => {
    localStorage.removeItem('cart_id')
    setCart(undefined)
  }, [])

  const createCart = useCallback(async () => {
    const api = getClientAPI()
    try {
      const { cart: cartData } = await api.store.cart.create({
        metadata: {
          locale: currentLocale,
        },
      }, cartSelectParams)
      localStorage.setItem('cart_id', cartData.id ?? '')
      setCart(cartData)
      return cartData.id ?? null
    } catch (error) {
      console.error('Error creating cart:', error)
      throw error
    }
  }, [currentLocale])

  const addItemOnce = useCallback(async (variantId: string, quantity: number, metadata?: Record<string, string>) => {
    const api = getClientAPI()
    let cartId = localStorage.getItem('cart_id')

    if (!cartId) {
      cartId = await createCart()
    }
    if (!cartId) throw new Error('No cart id')

    const { cart: updatedCart } = await api.store.cart.createLineItem(cartId, {
      variant_id: variantId,
      metadata: {
        ...(metadata ?? {}),
      },
      quantity,
    },
      cartSelectParams
    )

    setCart(updatedCart)
    toast.success(t('itemAdded'))
  }, [createCart, t])

  const addItem = useCallback(async (variantId: string, quantity: number, metadata?: Record<string, string>) => {
    try {
      await addItemOnce(variantId, quantity, metadata)
    } catch (error) {
      console.error('Error adding item to cart:', error)
      refreshCart()
      try {
        await addItemOnce(variantId, quantity, metadata)
      } catch (error) {
        console.error('Error adding item to cart:', error)
        toast.error('Failed to add item to cart')
      }
    }
  }, [addItemOnce, refreshCart])

  const removeItem = useCallback(async (lineItemId: string) => {
    const api = getClientAPI()
    try {
      const cartId = localStorage.getItem('cart_id')
      if (!cartId) throw new Error('No cart found')

      const { parent: updatedCart } = await api.store.cart.deleteLineItem(cartId, lineItemId, cartSelectParams)

      setCart(updatedCart)
      toast.success(t('itemRemoved'))
    } catch (error) {
      console.error('Error removing item from cart:', error)
      toast.error(t('itemRemovedFailed'))
    }
  }, [t])

  const updateItemQuantity = useCallback(async (lineItemId: string, quantity: number) => {
    const api = getClientAPI()
    try {
      const cartId = localStorage.getItem('cart_id')
      if (!cartId) throw new Error('No cart found')

      const { cart: updatedCart } = await api.store.cart.updateLineItem(cartId, lineItemId, {
        quantity,
      }, cartSelectParams)

      setCart(updatedCart)
      toast.success(t('itemQuantityUpdated'))
    } catch (error) {
      console.error('Error updating item quantity:', error)
      toast.error(t('itemQuantityUpdatedFailed'))
    }
  }, [t])

  const updateItem = useCallback(async (lineItemId: string, quantity: number) => {
    const api = getClientAPI()
    try {
      const cartId = localStorage.getItem('cart_id')
      if (!cartId) throw new Error('No cart found')

      if (quantity === 0) {
        await removeItem(lineItemId); return
      }

      const { cart: updatedCart } = await api.store.cart.updateLineItem(cartId, lineItemId, {
        quantity,
      },
        cartSelectParams
      )

      setCart(updatedCart)
      toast.success(t('itemQuantityUpdated'))
    } catch (error) {
      console.error('Error updating item quantity:', error)
      toast.error(t('itemQuantityUpdatedFailed'))
    }
  }, [removeItem, t])

  const applyPromoCode = useCallback(async (code: string) => {
    const api = getClientAPI()
    const cartId = localStorage.getItem('cart_id')
    if (!cartId) throw new Error('No cart found')

    try {
      const { cart: updatedCart } = await api.store.cart.update(cartId, {
        promo_codes: [code],
      }, cartSelectParams)
      // @ts-ignore
      const appliedPromotions: HttpTypes.StorePromotion[] = updatedCart.promotions ?? []
      setCart(updatedCart)

      if (!appliedPromotions.length) {
        toast.error(t('promoCodeNotFound'))
        return false
      }

      toast.success(t('promoCodeApplied'))
      return true
    } catch (error) {
      console.error('Error applying promo code:', error)
      toast.error(t('promoCodeNotFound'))
      return false
    }
  }, [t])

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        refreshCart,
        addItem,
        removeItem,
        updateItem,
        updateItemQuantity,
        applyPromoCode,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)

  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }

  return context
}
